/**
 * Planner Campaign API — wired to the real Influencer API Service (FastAPI).
 * See PLANNER_CAMPAIGN_API.md for full API contract.
 *
 * All functions now delegate to src/api_handlers/influencerCampaign.js.
 * Field mapping is handled here so UI components need no changes.
 */

import {
  listObjectives,
  listCampaigns,
  createInfluencerCampaign,
  getCampaignDetail,
  updateCampaign,
  archiveCampaign,
  completeCampaign,
  listShortlistedCreators,
  shortlistCreator,
  listCampaignVideos,
  linkVideoToCreator,
  unlinkCampaignVideo,
  syncVideo,
  updateVideoInsights,
  getCampaignReport,
  cancelCreatorDeal,
  activateDeal,
  completeDeal,
  validateBrandChannel,
} from 'api_handlers/influencerCampaign';

// ---------------------------------------------------------------------------
// Field mappers — API shape → UI shape
// ---------------------------------------------------------------------------

/**
 * Maps new API campaign object to the shape the UI components expect.
 * New API uses snake_case + `title`; UI uses camelCase + `name`.
 */
function calcDuration(startDate, endDate) {
  if (!startDate || !endDate) return null;
  const diff = new Date(endDate) - new Date(startDate);
  if (isNaN(diff) || diff < 0) return null;
  return Math.round(diff / (1000 * 60 * 60 * 24));
}

/**
 * First non-empty value from a BrandConnect (or platform) object that may use
 * camelCase (YouTube BrandConnect JSON) or snake_case (DB / some API layers).
 * Skips undefined, null, and blank strings. Allows 0 and false.
 */
export function pickBrandConnectField(obj, ...keys) {
  if (obj == null) return undefined;
  for (const k of keys) {
    if (!Object.prototype.hasOwnProperty.call(obj, k)) continue;
    const v = obj[k];
    if (v === undefined || v === null) continue;
    if (typeof v === 'string' && v.trim() === '') continue;
    return v;
  }
  for (const k of keys) {
    if (!Object.prototype.hasOwnProperty.call(obj, k)) continue;
    const v = obj[k];
    if (v === 0 || v === false) return v;
  }
  return undefined;
}

/**
 * Actual API response shape (from bc-api.influencer.in):
 * {
 *   id, name, desc, start_date, end_date, budget, status,
 *   total_posts, total_views, total_engagement_rate,
 *   bc_response: raw YouTube BrandConnect JSON (camelCase), e.g.
 *     campaignCode, brandChannelId, brandChannelHandle, brief, environment, state,
 *     brand, title, startDate/endDate or nested start_date objects, etc.
 *   Some layers or older docs used snake_case aliases — map below accepts both.
 * }
 */
function mapCampaignFromApi(c) {
  if (!c) return null;

  const startDate = c.start_date || '';
  const endDate = c.end_date || '';
  const duration = calcDuration(startDate, endDate);

  // bc_response present → this is a BrandConnect campaign (also accept bcResponse)
  const bc = c.bc_response || c.bcResponse || null;
  const bcEnabled = !!bc;

  const numOrNull = (v) => {
    if (v == null || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const firstNumber = (...values) => {
    for (const v of values) {
      const n = numOrNull(v);
      if (n != null) return n;
    }
    return null;
  };

  const requiredFromPlatformData = Array.isArray(c.platform_data)
    ? c.platform_data.reduce((sum, p) => sum + (numOrNull(p?.required) || 0), 0)
    : null;

  const deliveredFromPlatformData = Array.isArray(c.platform_data)
    ? c.platform_data.reduce((sum, p) => sum + (numOrNull(p?.delivered) || 0), 0)
    : null;

  const totalViews = firstNumber(c.total_views, c.linked_videos_views, 0) || 0;
  const organicViews = firstNumber(c.organic_views, c.linked_organic_views);
  const organicPercent = firstNumber(
    c.organic_percent,
    c.organic_reach_percent,
    totalViews > 0 && organicViews != null ? Math.round((organicViews / totalViews) * 100) : null,
  );
  const paidViews = firstNumber(
    c.linked_videos_views,
    totalViews && organicViews != null ? totalViews - organicViews : null,
  );

  return {
    id: c.id,
    name: c.name || c.title || '',
    objective: c.desc || c.objective || c.description || '',
    // Lifecycle: prefer top-level `status` when it is terminal (completed/archived);
    // otherwise BC state, then top-level. Avoids showing ACTIVE when API already says completed.
    status: resolveCampaignStatusForUi(c, bcEnabled, bc),
    startDate,
    endDate,
    // BC fields — sourced from nested bc_response (Google camelCase or snake_case re-exports)
    bcEnabled,
    bcCampaignCode: pickBrandConnectField(bc, 'campaign_code', 'campaignCode') ?? null,
    bcBrief: pickBrandConnectField(bc, 'brief') ?? null,
    bcEnvironment: pickBrandConnectField(bc, 'environment') ?? null,
    /** Human-readable brand name in BC (e.g. product line / advertiser string) */
    brandName: pickBrandConnectField(bc, 'brand', 'brandName', 'displayName') ?? null,
    /** @handle for the brand YouTube channel — only real handle fields, not `brand` string */
    brandChannelHandle: pickBrandConnectField(bc, 'brand_channel_handle', 'brandChannelHandle') ?? null,
    brandChannelId: pickBrandConnectField(bc, 'brand_channel_id', 'brandChannelId') ?? null,
    /** BC resource name (e.g. brands/.../campaigns/...) */
    bcResourceName: pickBrandConnectField(bc, 'name', 'resourceName') ?? null,
    bcTitle: pickBrandConnectField(bc, 'title', 'displayTitle') ?? null,
    // Platforms — API has no platforms array; inject top-level fields into youtube slot
    platforms: mapPlatformsFromApi(c.platforms, {
      budget: c.budget,
      totalPosts: c.total_posts,
      duration,
    }),
    // BC aggregated stats — use top-level totals when bc_response exists
    bcData: c.bc_data || c.bcData || (bcEnabled ? {
      // Keep both keys to support old and new dashboard cards.
      views: totalViews,
      totalViews,
      totalLikes: firstNumber(c.total_likes),
      totalComments: firstNumber(c.total_comments),
      liveVideos: firstNumber(c.linked_videos_count, deliveredFromPlatformData),
      engagementRate: firstNumber(c.total_engagement_rate, c.linked_engagement_rate, 0) || 0,
      avgEngagementRate: firstNumber(c.linked_engagement_rate, c.total_engagement_rate, 0) || 0,
      // Linked metrics are provided by detail payload in this backend shape.
      videosLinked: firstNumber(c.linked_videos_count, deliveredFromPlatformData),
      videosTotal: firstNumber(c.total_linked_count, requiredFromPlatformData, c.total_posts),
      // Organic reach is backed by organic_views; organicPercent is derived when possible.
      organicPercent,
      organicViews: organicViews,
      paidViews,
    } : null),
  };
}

function mapPlatformsFromApi(platforms, topLevel = {}) {
  let result = {};

  if (Array.isArray(platforms) && platforms.length > 0) {
    result = platforms.reduce((acc, p) => {
      const key = p.platform;
      acc[key] = acc[key] || {};
      acc[key].postType = p.post_type || p.postType;
      acc[key].expectedVideos = (acc[key].expectedVideos || 0) + (p.count || p.expectedVideos || 0);
      if (p.budget != null) acc[key].budget = p.budget;
      if (p.duration != null) acc[key].duration = p.duration;
      return acc;
    }, {});
  } else if (platforms && !Array.isArray(platforms)) {
    result = platforms;
  }

  // Inject top-level fields (budget, total_posts, duration) into the primary platform slot.
  // Falls back to 'youtube' if no platform data exists yet.
  const { budget, totalPosts, duration } = topLevel;
  if (budget != null || totalPosts != null || duration != null) {
    const primaryPlatform = result.youtube ? 'youtube' : (Object.keys(result)[0] || 'youtube');
    result[primaryPlatform] = result[primaryPlatform] || {};
    const slot = result[primaryPlatform];
    if (budget != null && slot.budget == null) slot.budget = budget;
    if (totalPosts != null && slot.expectedVideos == null) slot.expectedVideos = totalPosts;
    if (duration != null && slot.duration == null) slot.duration = duration;
  }

  return result;
}

/**
 * Brand Connect campaign/deal identifiers must be RFC 1034 DNS labels:
 * first char a–z, last char a–z or 0–9, interior a–z, 0–9, hyphen, max 63 chars.
 */
export function sanitizeBrandConnectCampaignTitle(raw) {
  const fallback = 'campaign';
  if (raw == null) return fallback;

  let s = String(raw)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');

  if (!s) return fallback;

  if (!/^[a-z]/.test(s)) {
    s = `c-${s}`;
  }
  s = s.replace(/-+$/g, '');
  if (!s) return fallback;
  if (!/[a-z0-9]$/.test(s)) {
    s += '0';
  }

  if (s.length > 63) {
    s = s.slice(0, 63);
    while (s.length > 0 && !/[a-z0-9]$/.test(s)) {
      s = s.slice(0, -1);
    }
    while (s.length > 0 && !/^[a-z]/.test(s)) {
      s = s.slice(1);
    }
  }

  if (!s || !/^[a-z]/.test(s) || !/[a-z0-9]$/.test(s)) return fallback;

  return s;
}

/**
 * Maps UI campaign payload → new API shape.
 */
function mapCampaignToApi(payload) {
  const rawTitle = payload.name || payload.title || '';
  const bcCreate = !!(payload.createAsBrandConnect || payload.bc_tracking_enabled);
  const title = bcCreate ? sanitizeBrandConnectCampaignTitle(rawTitle) : rawTitle;

  return {
    title,
    start_date: payload.startDate || payload.start_date || '',
    end_date: payload.endDate || payload.end_date || '',
    objective_id: payload.objective_id || payload.objectiveId || null,
    // Platforms: convert object → array
    platforms: mapPlatformsToApi(payload.platforms),
    // BC fields (only included if present)
    ...(payload.createAsBrandConnect || payload.bc_tracking_enabled
      ? {
          bc_tracking_enabled: true,
          brand_channel_handle: payload.brandChannelHandle || payload.brand_channel_handle || '',
          brand_channel_id: payload.brandChannelId || payload.brand_channel_id || '',
          bc_brief: payload.bcBrief || payload.bc_brief || '',
          bc_environment: payload.bcEnvironment || payload.bc_environment || 'TEST',
        }
      : {}),
  };
}

function mapPlatformsToApi(platforms) {
  if (!platforms) return [];
  if (Array.isArray(platforms)) return platforms;
  // Convert object form to array: {youtube: {expectedVideos: 3, postType: 'post'}} → [{platform, post_type, count}]
  return Object.entries(platforms).map(([platform, cfg]) => ({
    platform,
    post_type: cfg.postType || cfg.post_type || 'post',
    count: cfg.expectedVideos || cfg.count || 1,
  }));
}

// Strip "STATE_" prefix → "ACTIVE", "COMPLETED", etc.
function normalizeStatus(s) {
  if (!s) return 'ACTIVE';
  return String(s).replace(/^STATE_/, '');
}

/**
 * Single status string for UI (lowercase: active | draft | completed | archived).
 * Top-level campaign `status` wins for terminal states — `bc_response.state` can lag
 * (e.g. platform `completed` while BC still shows STATE_ACTIVE).
 */
function resolveCampaignStatusForUi(c, bcEnabled, bc) {
  const top = String(c.status || '')
    .trim()
    .toLowerCase()
    .replace(/^state_/, '');

  if (top === 'completed' || top === 'complete') return 'completed';
  if (top === 'archived' || top === 'archive') return 'archived';
  if (top === 'running' || top === 'live') return 'active';
  if (top === 'active') return 'active';
  if (top === 'draft') return 'draft';

  if (bcEnabled && bc?.state) {
    const bcNorm = normalizeStatus(bc.state).toLowerCase();
    if (bcNorm === 'completed' || bcNorm === 'complete') return 'completed';
    if (bcNorm === 'archived' || bcNorm === 'archive') return 'archived';
    if (bcNorm === 'active') return 'active';
    return bcNorm || 'draft';
  }

  if (top) return top;
  return 'draft';
}

// Strip "CONSENT_STATE_" prefix → "ACTIVE", "PENDING", "DECLINED"
function normalizeConsent(s) {
  if (!s) return 'PENDING';
  return String(s).replace(/^CONSENT_STATE_/, '');
}

function mapDealFromApi(d) {
  if (!d) return null;
  const bc = d.bc_response || d.bcResponse || {};
  // BC deal lifecycle (ACTIVE / COMPLETED / …) — separate from workflow status (shortlisted, etc.)
  // Prefer platform `bc_deal_state`; `bc_response.state` can lag (e.g. canceled locally while BC still ACTIVE).
  const bcLifecycleRaw =
    d.bc_deal_state != null && String(d.bc_deal_state).trim() !== ''
      ? d.bc_deal_state
      : pickBrandConnectField(bc, 'state', 'State');
  const dealStatus =
    bcLifecycleRaw != null && String(bcLifecycleRaw).trim() !== ''
      ? normalizeStatus(bcLifecycleRaw)
      : undefined;

  const cc = bc.creator_cost || bc.creatorCost;
  const costUnitsFromBc =
    cc != null
      ? Number(
          pickBrandConnectField(cc, 'units', 'Units') ?? cc.units,
        )
      : null;
  const costUnits =
    costUnitsFromBc != null && !Number.isNaN(costUnitsFromBc)
      ? costUnitsFromBc
      : d.creator_cost_units != null
        ? Number(d.creator_cost_units)
        : null;
  const costAmount =
    costUnits != null && !Number.isNaN(costUnits)
      ? costUnits
      : d.final_price ?? d.cost?.amount ?? 0;

  const costCurrencyFromBc = cc
    ? pickBrandConnectField(cc, 'currency_code', 'currencyCode') ?? 'INR'
    : null;

  return {
    id: d.id,
    creatorName: d.creator_name || d.creatorName || d.channel_title || d.influencer_name || '',
    channelId: d.creator_channel_id || d.creatorChannelId || d.channelId || '',
    // Workflow column in UI (shortlisted, running, …) — do not replace with BC deal state
    status: String(d.status || 'active').trim().toLowerCase(),
    dealStatus,
    // DealRow uses fmtC(deal.cost) — flat number
    cost: costAmount,
    costCurrency: costCurrencyFromBc || d.creator_cost_currency || d.cost?.currency || 'INR',
    // Consent is not the same as bc_deal_state (deal lifecycle)
    bcConsent: normalizeConsent(
      d.consent_status || d.consentStatus || d.bc_consent_state || d.consent_state || '',
    ),
    expectedVideos: pickBrandConnectField(bc, 'expected_video_count', 'expectedVideoCount')
      ?? d.expected_video_count
      ?? d.expectedVideos
      ?? 0,
    deliveredVideos: d.video_count ?? d.delivered_videos ?? d.deliveredVideos ?? 0,
    partnershipTerms:
      d.partnership_terms
      || pickBrandConnectField(bc, 'partnership_terms', 'partnershipTerms')
      || '',
    createdAt: d.created_at || d.createdAt || new Date().toISOString(),
    dealLink: d.deal_link || d.dealLink || pickBrandConnectField(bc, 'deal_link', 'dealLink') || null,
    dealId: pickBrandConnectField(bc, 'deal_id', 'dealId') ?? null,
    thumbnailUrl: d.thumbnail_url || null,
    subscriberCount: d.subscriber_count ?? null,
    channelViewCount: d.channel_view_count ?? null,
    channelVideoCount: d.channel_video_count ?? null,
    bc_response: d.bc_response || d.bcResponse,
    bc_deal_state: d.bc_deal_state,
  };
}

// "linked" → "ACTIVE", "pending" / anything else → "PENDING"
function normalizeLinkState(s) {
  if (!s) return 'PENDING';
  const lower = String(s).toLowerCase();
  if (lower === 'linked') return 'ACTIVE';
  if (lower === 'declined') return 'DECLINED';
  return 'PENDING';
}

/**
 * Actual API response shape (GET /api/v1/campaigns/:id/videos):
 * {
 *   id, youtube_video_id, title, view_count, paid_view_count,
 *   like_count, paid_like_count, comment_count, paid_comment_count,
 *   is_bc_linked, link_state, creator_channel_id,
 *   bc_response: { name, state, environment },
 *   created_at
 * }
 */
function mapSplit(s) {
  if (!s) return null;
  return {
    views: s.views ?? 0,
    engagedViews: s.engaged_views ?? 0,
    watchTimeMinutes: s.watch_time_minutes ?? 0,
    avgViewDurationSecs: s.avg_view_duration_secs ?? 0,
    uniqueViewers: s.unique_viewers ?? null,
    likes: s.likes ?? 0,
    comments: s.comments ?? 0,
    shares: s.shares ?? 0,
    playlistAdds: s.playlist_adds ?? 0,
    engagementRate: s.engagement_rate ?? null,
  };
}

/**
 * Google Ads "Partnership Ads" paid stats — a separate data block alongside the
 * BrandConnect organic/paid split. Present on video list rows, the video
 * detail/insights response, and (separately, un-mapped here) the campaign report.
 * `spend` is already in whole currency units.
 */
function mapGoogleAds(ga) {
  if (!ga) return null;
  return {
    views: ga.views ?? 0,
    impressions: ga.impressions ?? 0,
    clicks: ga.clicks ?? 0,
    spend: ga.spend ?? 0,
    conversions: ga.conversions ?? 0,
  };
}

function mapVideoFromApi(v) {
  if (!v) return null;

  const org = mapSplit(v.organic);
  const paid = mapSplit(v.paid);
  const total = mapSplit(v.total);

  const vbc = v.bc_response || v.bcResponse;

  const views = v.views ?? total?.views ?? v.view_count ?? 0;
  const likes = v.likes ?? total?.likes ?? v.like_count ?? 0;
  const comments = v.comments ?? total?.comments ?? v.comment_count ?? 0;
  const shares = v.shares ?? total?.shares ?? 0;

  const organicViews = org?.views ?? null;
  const paidViews = paid?.views ?? null;
  const totalViews = total?.views ?? views;
  const organicPct = totalViews > 0 && organicViews != null ? Math.round((organicViews / totalViews) * 100) : null;
  const paidPct = organicPct != null ? 100 - organicPct : null;

  return {
    id: v.id,
    title: v.title || v.youtube_video_id || '',
    creatorName: v.creator_name || v.creatorName || '',
    channelId: v.creator_channel_id || v.channelId || '',
    youtubeVideoId: v.youtube_video_id || v.youtubeVideoId || '',
    // Normalize link_state → ACTIVE / PENDING / DECLINED
    consentStatus: normalizeLinkState(v.link_state || v.consent_status),
    publishedAt: v.publish_date || (v.created_at ? v.created_at.split('T')[0] : ''),
    durationSecs: v.duration_secs ?? v.durationSecs ?? null,
    hasProductPlacement: v.has_product_placement ?? v.hasProductPlacement ?? false,
    brandMentions: Array.isArray(v.brand_mentions) ? v.brand_mentions : (Array.isArray(v.brandMentions) ? v.brandMentions : []),
    bcState: (vbc && pickBrandConnectField(vbc, 'state', 'State')) || null,
    // top-level metrics (used for table view)
    metrics: {
      views,
      likes,
      comments,
      shares,
      engagedViews: total?.engagedViews ?? v.engaged_views ?? 0,
      watchTimeHours: total ? total.watchTimeMinutes / 60 : (v.watch_time_hours ?? 0),
      avgViewDurationSecs: total?.avgViewDurationSecs ?? v.avg_view_duration_secs ?? 0,
      avgViewPercentage: v.avg_view_percentage ?? 0,
      engagementRate: total?.engagementRate ?? (views > 0 ? (likes + comments) / views : 0),
    },
    // organic / paid / total split objects
    organic: org,
    paid,
    total,
    // traffic split percentages for table display
    trafficSplit: {
      organic: organicPct,
      paid: paidPct,
      organicViews,
      paidViews,
    },
    // audience demographics
    ageDistribution: v.age_distribution ?? null,
    genderDistribution: v.gender_distribution ?? null,
    countryDistributionBySource: v.country_distribution_by_source ?? null,
    shoppingInterests: v.shopping_interests ?? null,
    watchingInterests: v.watching_interests ?? null,
    // Google Ads "Partnership Ads" — separate paid-stats block; null when absent.
    googleAds: mapGoogleAds(v.google_ads),
    promoted: v.promoted === true,
  };
}

// ---------------------------------------------------------------------------
// Public API functions (called by UI components)
// ---------------------------------------------------------------------------

/**
 * List campaigns (My Campaigns page).
 * Real API: GET /api/v1/campaigns/
 */
export function getPlannerCampaigns(params = {}) {
  return listCampaigns().then((data) => {
    const campaigns = (data.results || data.campaigns || data || []).map(mapCampaignFromApi);
    return { campaigns, total: data.count || campaigns.length };
  });
}

/**
 * Get single campaign detail.
 * Real API: GET /api/v1/campaigns/:campaignId
 */
export function getPlannerCampaignById(campaignId) {
  return getCampaignDetail(campaignId).then((data) => {
    if (!data) return null;
    const campaign = mapCampaignFromApi(data);
    const deals = (data.shortlisted_creators || data.deals || []).map(mapDealFromApi);
    const videos = (data.videos || []).map(mapVideoFromApi);
    return { ...campaign, deals, videos };
  });
}

/**
 * Create campaign.
 * Real API: POST /api/v1/campaigns/
 */
export function createPlannerCampaign(payload) {
  return createInfluencerCampaign(mapCampaignToApi(payload)).then(mapCampaignFromApi);
}

/**
 * Update campaign fields.
 * Real API: PATCH /api/v1/campaigns/:campaignId
 */
export function updatePlannerCampaign(campaignId, payload) {
  const apiPayload = {};

  if (payload.name != null || payload.title != null) {
    apiPayload.title = payload.name || payload.title || '';
  }
  if (payload.startDate != null || payload.start_date != null) {
    apiPayload.start_date = payload.startDate || payload.start_date || '';
  }
  if (payload.endDate != null || payload.end_date != null) {
    apiPayload.end_date = payload.endDate || payload.end_date || '';
  }
  if (payload.objective_id != null || payload.objectiveId != null) {
    apiPayload.objective_id = Number(payload.objective_id || payload.objectiveId);
  }
  if (payload.objective != null || payload.desc != null) {
    apiPayload.desc = payload.objective || payload.desc || '';
  }

  return updateCampaign(campaignId, apiPayload).then(mapCampaignFromApi);
}

/**
 * Mark campaign complete.
 * Real API: POST /api/v1/campaigns/:campaignId/complete
 */
export function completePlannerCampaign(campaignId) {
  return completeCampaign(campaignId);
}

/**
 * Archive campaign.
 * Real API: DELETE /api/v1/campaigns/:campaignId/archive
 */
export function archivePlannerCampaign(campaignId) {
  return archiveCampaign(campaignId);
}

/**
 * Cancel a creator deal.
 * Real API: POST /api/v1/campaigns/:campaignId/creators/:shortlistedId/cancel-deal
 */
export function cancelPlannerCreatorDeal(campaignId, shortlistedId) {
  return cancelCreatorDeal(campaignId, shortlistedId);
}

/**
 * Activate / re-activate a deal.
 * Real API: POST /api/v1/campaigns/:campaignId/deals/:dealId/activate
 */
export function activatePlannerDeal(campaignId, dealId) {
  return activateDeal(campaignId, dealId);
}

/**
 * Complete a deal.
 * Real API: POST /api/v1/campaigns/:campaignId/deals/:dealId/complete
 */
export function completePlannerDeal(campaignId, dealId) {
  return completeDeal(campaignId, dealId);
}

/**
 * List deals for a campaign.
 * Real API: GET /api/v1/campaigns/:campaignId/creators
 */
export function getPlannerCampaignDeals(campaignId) {
  return listShortlistedCreators(campaignId).then((data) => {
    const deals = (data.results || data || []).map(mapDealFromApi);
    return { deals, total: data.count || deals.length };
  });
}

/**
 * Shortlist a creator for a campaign (creates BC deal if BC-enabled).
 * Real API: POST /api/v1/campaigns/:campaignId/creators
 */
export function shortlistPlannerCreator(campaignId, payload) {
  const apiPayload = {
    influencer_id: payload.influencerId || payload.influencer_id,
    creator_channel_id: payload.channelId || payload.creator_channel_id,
    final_price: payload.cost?.amount || payload.final_price || 0,
    expected_video_count: payload.expectedVideos || payload.expected_video_count || 1,
    creator_cost_units: payload.cost?.amount || payload.creator_cost_units || 0,
    creator_cost_currency: payload.cost?.currency || payload.creator_cost_currency || 'INR',
    partnership_terms: payload.partnershipTerms || payload.partnership_terms || '',
    ...(payload.dealLink ? { deal_link: payload.dealLink } : {}),
  };
  return shortlistCreator(campaignId, apiPayload).then(mapDealFromApi);
}

/**
 * List video collaborations for a campaign.
 * Real API: GET /api/v1/campaigns/:campaignId/videos
 */
export function getPlannerCampaignVideos(campaignId) {
  return listCampaignVideos(campaignId).then((data) => {
    const items = (data.results || data.videos || data || []).map(mapVideoFromApi);
    return { items, total: data.count || items.length };
  });
}

/**
 * Link a YouTube video to a shortlisted creator.
 * Real API: POST /api/v1/campaigns/:campaignId/creators/:shortlistedId/videos
 */
export function linkPlannerVideo(campaignId, shortlistedId, youtubeVideoId) {
  return linkVideoToCreator(campaignId, shortlistedId, youtubeVideoId).then(mapVideoFromApi);
}

/**
 * Unlink a video collaboration from campaign.
 * Real API: DELETE /api/v1/campaigns/:campaignId/videos/:youtubeVideoId
 */
export function unlinkPlannerVideo(campaignId, youtubeVideoId) {
  return unlinkCampaignVideo(campaignId, youtubeVideoId);
}

/**
 * Get aggregated organic/paid video report for a campaign.
 * Real API: GET /api/v1/campaigns/:campaignId/report
 */
export function getPlannerCampaignReport(campaignId) {
  return getCampaignReport(campaignId);
}

/**
 * Sync a video's BrandConnect data from YouTube.
 * Real API: POST /api/v1/videos/:youtubeVideoId/bc/sync
 */
export function syncPlannerVideo(youtubeVideoId) {
  return syncVideo(youtubeVideoId);
}

/**
 * Update BC paid metrics on a video (from BrandConnect insights).
 * Real API: PUT /api/v1/campaigns/:campaignId/videos/insights
 */
export function updatePlannerVideoInsights(campaignId, insightsPayload) {
  return updateVideoInsights(campaignId, insightsPayload);
}

/**
 * List campaign objectives.
 * Real API: GET /api/v1/objectives/
 */
export function getPlannerObjectives() {
  return listObjectives().then((data) => Array.isArray(data) ? data : (data?.results || []));
}

export async function isValidBrandChannelHandle(brand_channel_handle, brand_channel_id) {
  try {
    const res = await validateBrandChannel({
      brand_channel_handle,
      brand_channel_id: brand_channel_id,
    });
    console.log('Validation result for handle', brand_channel_handle, res);
    return {"valid": res?.valid === true, "match": res?.match === true};
  } catch (error) {
    return false;
  }
}

// export async function isValidBrandChannelId(brand_channel_id) {
//   try {
//     const res = await validateBrandChannel({
//       brand_channel_handle: null,
//       brand_channel_id,
//     });
//     console.log('Validation result for ID', brand_channel_id, res);
//     return res?.valid === true;
//   } catch (error) {
//     return false;
//   }
// }

export async function isBrandChannelMatch(
  brand_channel_handle,
  brand_channel_id
) {
  try {
    const res = await validateBrandChannel({
      brand_channel_handle,
      brand_channel_id,
    });
    console.log('Validation result for handle-ID match', { brand_channel_handle, brand_channel_id }, res);
    return res?.match === true;
  } catch (error) {
    return false;
  }
}